/**
 * @file Pont evenements du core -> vibrations (platform/haptics). Seulement les moments ou le joueur AGIT ou RECOIT quelque chose
 * d'important : jamais sur les tirs, impacts ou eliminations (trop frequents, fatigant et gourmand en batterie).
 *
 * Achat d'amelioration manuel -> 'light' (« Achat max » de plusieurs niveaux -> 'medium') ; achats de l'amelioration AUTO -> rien ;
 * fusion manuelle -> 'medium' (fusion auto -> 'tick', espacees) ; tirage -> 'light' (grade S ou mieux -> 'success') ; achat
 * cosmetique (skin, aura, feu d'artifice, effet de tir, munition) -> 'success' ; amelioration d'etoile -> 'success' / 'warning' ;
 * niveau gagne -> 'success' ; niveau perdu -> 'warning' ; renaissance -> 'heavy' ; recompense reclamee (mission, passe, cadeau
 * quotidien) -> 'light'. Une meme intensite n'est rejouee qu'apres MIN_GAP_MS (maintien du bouton d'achat = rafale d'achats).
 */
import type { EventBus, GameEvents } from '@core/index';
import type { HapticKind, HapticsSink } from '@platform/haptics';
import { GRADES } from '@shared/enums';

/** Ecart minimal entre deux vibrations de meme intensite (ms). */
export const HAPTIC_MIN_GAP_MS = 70;
/** Ecart minimal entre deux vibrations de fusion AUTO (ms) : la fusion pas-a-pas en enchaine plusieurs. */
export const HAPTIC_AUTO_MERGE_GAP_MS = 400;
/** Grade a partir duquel un tirage merite une vibration « succes ». */
const BIG_DRAW_GRADE = GRADES.indexOf('S');

const nowMs = (): number => (typeof performance !== 'undefined' ? performance.now() : 0);

export const bindHaptics = (bus: EventBus<GameEvents>, sink: HapticsSink, now: () => number = nowMs): (() => void) => {
  const last = new Map<string, number>();
  const play = (kind: HapticKind, key: string = kind, gap = HAPTIC_MIN_GAP_MS): void => {
    const t = now();
    if (t - (last.get(key) ?? -Infinity) < gap) return;
    last.set(key, t);
    sink.play(kind);
  };
  const success = (): void => play('success');

  const offs: Array<() => void> = [
    bus.on('UpgradeBought', (e) => {
      if (e.auto) return;
      play(e.count > 1 ? 'medium' : 'light');
    }),
    bus.on('WeaponsMerged', (e) => (e.auto ? play('tick', 'autoMerge', HAPTIC_AUTO_MERGE_GAP_MS) : play('medium'))),
    bus.on('WeaponDrawn', (e) => (GRADES.indexOf(e.bestGrade) >= BIG_DRAW_GRADE ? success() : play('light'))),
    bus.on('SkinBought', success),
    bus.on('PetBought', success),
    bus.on('AuraBought', success),
    bus.on('FireworkBought', success),
    bus.on('ShotFxBought', success),
    bus.on('AmmoSkinBought', success),
    bus.on('StarUpgraded', (e) => play(e.success ? 'success' : 'warning')),
    bus.on('LevelCleared', success),
    bus.on('LevelFailed', () => play('warning')),
    bus.on('RebirthDone', () => play('heavy')),
    bus.on('MissionClaimed', () => play('light')),
    bus.on('PassTierClaimed', () => play('light')),
    bus.on('DailyClaimed', () => play('light')),
  ];
  return () => {
    for (const off of offs) off();
  };
};
